import axios from 'axios';
import { getAuthToken } from '../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = '/api/reporting-hub/hardware';
const SW_BASE = '/api/reporting-hub/software';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Build the `filters` query (repeatable param) + keywords.
function buildParams({ keywords, filters, offset, limit }) {
  const params = new URLSearchParams();
  if (keywords) params.set('keywords', keywords);
  (filters || []).forEach((f) => params.append('filters', f));
  if (offset != null) params.set('offset', String(offset));
  if (limit != null) params.set('limit', String(limit));
  return params;
}

export const searchProjects = async ({ keywords = '', filters = [], offset = 0, limit = 10 }) => {
  const res = await api.get(`${BASE}/projects?${buildParams({ keywords, filters, offset, limit })}`);
  return res.data;
};

export const getSuggestedFilters = async ({ keywords = '', filters = [] } = {}) => {
  const res = await api.get(`${BASE}/filters?${buildParams({ keywords, filters })}`);
  return res.data;
};

export const getDashboard = async (projectId) => {
  const res = await api.get(`${BASE}/projects/${projectId}/dashboard`);
  return res.data;
};

export const createEmailDraft = async (projectId) => {
  const res = await api.post(`${BASE}/projects/${projectId}/email-draft`, {});
  return res.data;
};

// Default Hardware UI layout template (sections + widgets + image URLs).
export const getLayout = async () => {
  const res = await api.get(`${BASE}/layout`);
  return res.data;
};

// Generate the four widget images for a project (Python + matplotlib) and,
// when upload=true, upload them to S3 and return their URLs.
export const generateLayoutImages = async (projectId, upload = true) => {
  const res = await api.post(`${BASE}/projects/${projectId}/layout-images?upload=${upload}`, {});
  return res.data;
};

// Email recipient list (saved per project).
export const getRecipients = async (projectId) => {
  const res = await api.get(`${BASE}/projects/${projectId}/recipients`);
  return res.data;
};

export const saveRecipients = async (projectId, payload) => {
  const res = await api.put(`${BASE}/projects/${projectId}/recipients`, payload);
  return res.data;
};

// Build the email preview (recipients + rendered HTML body with inline charts).
export const getEmailPreview = async (projectId) => {
  const res = await api.get(`${BASE}/projects/${projectId}/email-preview`);
  return res.data;
};

// Download the .eml draft (uses saved recipients + current report view).
// Optionally pass an edited { bodyHtml, subject } from the preview.
export const downloadEmailDraft = async (projectId, payload = {}) => {
  const res = await api.post(`${BASE}/projects/${projectId}/email-draft`, payload, { responseType: 'blob' });
  const cd = res.headers['content-disposition'] || '';
  const match = cd.match(/filename="?([^"]+)"?/);
  const fileName = match ? match[1] : `report_${projectId}.eml`;
  const blob = new Blob([res.data], { type: 'message/rfc822' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  return { ok: true, fileName };
};

// ---- Software module ------------------------------------------------------

export const swSearchProjects = async ({ keywords = '', filters = [], offset = 0, limit = 10 }) => {
  const res = await api.get(`${SW_BASE}/projects?${buildParams({ keywords, filters, offset, limit })}`);
  return res.data;
};

export const swGetSuggestedFilters = async ({ keywords = '', filters = [] } = {}) => {
  const res = await api.get(`${SW_BASE}/filters?${buildParams({ keywords, filters })}`);
  return res.data;
};

export const swGetDashboard = async (projectId) => {
  const res = await api.get(`${SW_BASE}/projects/${projectId}/dashboard`);
  return res.data;
};

export const swGetLayout = async () => {
  const res = await api.get(`${SW_BASE}/layout`);
  return res.data;
};

// Software email
export const swGetRecipients = async (projectId) => {
  const res = await api.get(`${SW_BASE}/projects/${projectId}/recipients`);
  return res.data;
};

export const swSaveRecipients = async (projectId, payload) => {
  const res = await api.put(`${SW_BASE}/projects/${projectId}/recipients`, payload);
  return res.data;
};

export const swGetEmailPreview = async (projectId) => {
  const res = await api.get(`${SW_BASE}/projects/${projectId}/email-preview`);
  return res.data;
};

export const swDownloadEmailDraft = async (projectId, payload = {}) => {
  const res = await api.post(`${SW_BASE}/projects/${projectId}/email-draft`, payload, { responseType: 'blob' });
  const cd = res.headers['content-disposition'] || '';
  const match = cd.match(/filename="?([^"]+)"?/);
  const fileName = match ? match[1] : `sw_report_${projectId}.eml`;
  const blob = new Blob([res.data], { type: 'message/rfc822' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  return { ok: true, fileName };
};

// ---- Certification module -------------------------------------------------

const CERT_BASE = '/api/reporting-hub/certification';

// In-memory cache for certification data that rarely changes (report types + models list)
const _certCache = { reportTypes: null, models: null, modelsAt: 0 };
const CERT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const certGetReportTypes = async () => {
  if (_certCache.reportTypes) return _certCache.reportTypes;
  const res = await api.get(`${CERT_BASE}/report-types`);
  _certCache.reportTypes = res.data;
  return res.data;
};

export const certListProjects = async (keywords = '') => {
  // Cache only the full (no keyword) list — keyword searches always go to server
  if (!keywords && _certCache.models && Date.now() - _certCache.modelsAt < CERT_CACHE_TTL) {
    return _certCache.models;
  }
  const res = await api.get(`${CERT_BASE}/projects?keywords=${encodeURIComponent(keywords)}`);
  if (!keywords) {
    _certCache.models = res.data;
    _certCache.modelsAt = Date.now();
  }
  return res.data;
};

export const certWeeklyHighlight = async (model) => {
  const res = await api.get(`${CERT_BASE}/projects/${encodeURIComponent(model)}/weekly-highlight`);
  return res.data;
};

export const certCsCgTracking = async (model, year, week) => {
  const res = await api.get(`${CERT_BASE}/projects/${encodeURIComponent(model)}/cs-cg-tracking?year=${year}&week=${week}`);
  return res.data;
};

export const certDailyReport = async (model, category) => {
  const q = category ? `?category=${encodeURIComponent(category)}` : '';
  const res = await api.get(`${CERT_BASE}/projects/${encodeURIComponent(model)}/daily-report${q}`);
  return res.data;
};

export const certGetLayout = async (templateType) => {
  const res = await api.get(`${CERT_BASE}/layout/${templateType}`);
  return res.data;
};

export const certLogoOverview = async (model, upload = true) => {
  const res = await api.post(`${CERT_BASE}/projects/${encodeURIComponent(model)}/logo-overview?upload=${upload}`, {});
  return res.data;
};

export const certCharts = async (model, upload = true) => {
  const res = await api.post(`${CERT_BASE}/projects/${encodeURIComponent(model)}/charts?upload=${upload}`, {});
  return res.data;
};

// Certification email
export const certGetRecipients = async (model) => {
  const res = await api.get(`${CERT_BASE}/projects/${encodeURIComponent(model)}/recipients`);
  return res.data;
};

export const certSaveRecipients = async (model, payload) => {
  const res = await api.put(`${CERT_BASE}/projects/${encodeURIComponent(model)}/recipients`, payload);
  return res.data;
};

export const certGetEmailPreview = async (model, type, year, week) => {
  let q = `?type=${encodeURIComponent(type)}`;
  if (year) q += `&year=${year}`;
  if (week) q += `&week=${week}`;
  const res = await api.get(`${CERT_BASE}/projects/${encodeURIComponent(model)}/email-preview${q}`);
  return res.data;
};

export const certDownloadEmailDraft = async (model, payload = {}) => {
  const type = payload.type || 'bcst_daily';
  const res = await api.post(`${CERT_BASE}/projects/${encodeURIComponent(model)}/email-draft?type=${encodeURIComponent(type)}`, payload, { responseType: 'blob' });
  const cd = res.headers['content-disposition'] || '';
  const match = cd.match(/filename="?([^"]+)"?/);
  const fileName = match ? match[1] : `cert_report_${model}.eml`;
  const blob = new Blob([res.data], { type: 'message/rfc822' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  return { ok: true, fileName };
};
